/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { Switch } from "@/components/ui/switch";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownTrayIcon, ArrowLeftIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import {
  DockerContainerAction,
  DockerHost,
  getDockerContainerLogs,
  getDockerHostContainers,
  getDockerLiveContainers,
  inspectDockerContainer,
  runDockerContainerAction,
} from "@/api/docker";
import { showToast } from "@/components/common/Toast";
import { fmtDuration } from "@/components/servers/kit";
import { DockerStatsTab } from "../tabs/DockerStatsTab";
import type { ContainerTab } from "./ContainersView";
import { ConfirmDialog, PortLinks, StateBadge, fmtDate, fmtSize, hostAddress, shortID } from "./shared";

const SECRET_RE = /(pass|secret|token|key|pwd|credential|auth)/i;

const Row: React.FC<{ k: string; children?: React.ReactNode }> = ({ k, children }) =>
  children === undefined || children === null || children === "" ? null : (
    <>
      <dt>{k}</dt>
      <dd>{children}</dd>
    </>
  );

const LogsView: React.FC<{ host: DockerHost; containerId: string; name: string }> = ({ host, containerId, name }) => {
  const [tail, setTail] = useState(200);
  const [follow, setFollow] = useState(true);
  const [filter, setFilter] = useState("");
  const [wrap, setWrap] = useState(true);
  const { data = "", isLoading, isError, error } = useQuery({
    queryKey: ["docker-container-logs", host.id, containerId, tail],
    queryFn: () => getDockerContainerLogs(host.id, containerId, tail),
    refetchInterval: follow ? 3000 : false,
  });
  const lines = useMemo(() => {
    const all = data.split("\n").filter(Boolean);
    const f = filter.trim().toLowerCase();
    return f ? all.filter((l) => l.toLowerCase().includes(f)) : all;
  }, [data, filter]);

  const download = () => {
    const url = URL.createObjectURL(new Blob([data], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rt-box dkr-logs">
      <div className="dkr-logbar">
        <select value={tail} onChange={(e) => setTail(Number(e.target.value))} aria-label="Lines">
          {[100, 200, 500, 1000, 2000].map((n) => (
            <option key={n} value={n}>
              {n} lines
            </option>
          ))}
        </select>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter lines…" aria-label="Filter lines" />
        <label className="nx-switch-row">
          <Switch size="sm" checked={follow} onCheckedChange={setFollow} /> Auto-refresh
        </label>
        <label className="nx-switch-row">
          <Switch size="sm" checked={wrap} onCheckedChange={setWrap} /> Wrap
        </label>
        <button type="button" className="gh-btn" onClick={download} disabled={!data}>
          <ArrowDownTrayIcon /> Download
        </button>
        <span className="dkr-dim dkr-count">{lines.length} lines</span>
      </div>
      <pre className={`dkr-logbody${wrap ? " dkr-logbody--wrap" : ""}`}>
        {isLoading ? "Loading logs…" : isError ? (error as Error).message : lines.length ? lines.join("\n") : "No log output."}
      </pre>
    </div>
  );
};

interface Props {
  host: DockerHost;
  containerId: string;
  tab: ContainerTab;
  onTab: (t: ContainerTab) => void;
  onBack: () => void;
}

export const ContainerPage: React.FC<Props> = ({ host, containerId, tab, onTab, onBack }) => {
  const queryClient = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const [confirm, setConfirm] = useState<DockerContainerAction | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(t);
  }, []);

  const { data: live = [] } = useQuery({
    queryKey: ["docker-live", host.id],
    queryFn: () => getDockerLiveContainers(host.id),
    refetchInterval: 10_000,
  });
  const { data: cached = [] } = useQuery({
    queryKey: ["docker-host-containers", host.id],
    queryFn: () => getDockerHostContainers(host.id),
    retry: false,
  });
  const { data: info, isError, error } = useQuery({
    queryKey: ["docker-inspect", host.id, containerId],
    queryFn: () => inspectDockerContainer(host.id, containerId),
    refetchInterval: 10_000,
  });

  const row = live.find((c) => c.id === containerId);
  const monitored = cached.find((c) => c.containerId === containerId);
  const state = row?.state ?? info?.status ?? "unknown";
  const name = row?.name ?? info?.name ?? containerId.slice(0, 12);
  const address = hostAddress(host);

  const run = useMutation({
    mutationFn: (action: DockerContainerAction) => runDockerContainerAction(host.id, containerId, action),
    onSuccess: (_d, action) => {
      showToast(`${name}: ${action} done`, "success");
      queryClient.invalidateQueries({ queryKey: ["docker-live", host.id] });
      queryClient.invalidateQueries({ queryKey: ["docker-inspect", host.id, containerId] });
      queryClient.invalidateQueries({ queryKey: ["docker-host-containers", host.id] });
      setConfirm(null);
      if (action === "remove") onBack();
    },
    onError: (e: Error, action) => {
      showToast(`Could not ${action} ${name}`, "error", { description: e.message });
      setConfirm(null);
    },
  });

  const can = {
    start: state !== "running" && state !== "paused",
    stop: state === "running" || state === "restarting",
    kill: state === "running" || state === "paused" || state === "restarting",
    restart: state === "running" || state === "exited",
    pause: state === "running",
    unpause: state === "paused",
    remove: state !== "running" && state !== "paused" && state !== "restarting",
  };
  const btn = (action: DockerContainerAction, label: string, danger = false) => (
    <button
      key={action}
      type="button"
      className={`dkr-act${danger ? " dkr-act--danger" : ""}`}
      disabled={!can[action as keyof typeof can] || run.isPending}
      onClick={() => (action === "kill" || action === "remove" ? setConfirm(action) : run.mutate(action))}
    >
      {label}
    </button>
  );

  return (
    <div className="dkr-stack">
      <div className="rt-head">
        <button type="button" className="gh-btn" onClick={onBack}>
          <ArrowLeftIcon /> Containers
        </button>
      </div>
      <div className="rt-head">
        <div style={{ minWidth: 0 }}>
          <h2>{name}</h2>
          <p>
            {row?.image ?? info?.image} · {shortID(containerId)}
          </p>
        </div>
        <StateBadge state={state} />
        {info?.health && <span className={`dkr-state dkr-state--${info.health === "healthy" ? "ok" : info.health === "unhealthy" ? "bad" : "warn"}`}><i />{info.health}</span>}
      </div>

      <div className="dkr-actions" role="toolbar" aria-label="Container actions">
        {btn("start", "Start")}
        {btn("stop", "Stop")}
        {btn("kill", "Kill", true)}
        {btn("restart", "Restart")}
        {btn("pause", "Pause")}
        {btn("unpause", "Resume")}
        {btn("remove", "Remove", true)}
      </div>

      <div className="dkr-tabs" role="tablist">
        {(["details", "logs", "stats"] as ContainerTab[]).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className="dkr-tab" onClick={() => onTab(t)}>
            {t === "details" ? "Details" : t === "logs" ? "Logs" : "Stats"}
          </button>
        ))}
      </div>

      {tab === "logs" && <LogsView host={host} containerId={containerId} name={name} />}

      {tab === "stats" &&
        (monitored ? (
          <DockerStatsTab host={host} container={monitored} />
        ) : (
          <div className="rt-box">
            <p className="dkr-dim">This container isn't in the monitored set yet; statistics appear after the next poll.</p>
          </div>
        ))}

      {tab === "details" && (
        <>
          {isError && <div className="rt-error">{(error as Error).message}</div>}
          {info && (
            <div className="rt-grid">
              <div className="rt-box rt-c6">
                <h3>Container</h3>
                <dl className="rt-kv">
                  <Row k="ID">
                    <span className="dkr-mono">{info.id}</span>
                  </Row>
                  <Row k="Name">{info.name}</Row>
                  <Row k="Status">
                    {info.status}
                    {info.running && info.startedAt ? ` · up ${fmtDuration((now - Date.parse(info.startedAt)) / 1000)}` : ""}
                    {!info.running && info.exitCode ? ` · exit code ${info.exitCode}` : ""}
                    {info.oomKilled ? " · OOM killed" : ""}
                  </Row>
                  <Row k="Created">{fmtDate(info.created)}</Row>
                  <Row k="Started">{info.running ? fmtDate(info.startedAt) : undefined}</Row>
                  <Row k="Finished">{!info.running ? fmtDate(info.finishedAt) : undefined}</Row>
                  <Row k="Restarts">{String(info.restartCount)}</Row>
                  <Row k="Restart policy">{info.restartPolicy || "no"}</Row>
                  <Row k="Image">{info.image}</Row>
                  <Row k="Platform">{[info.platform, info.driver].filter(Boolean).join(" · ")}</Row>
                  <Row k="Host">{info.hostname}</Row>
                  <Row k="User">{info.user}</Row>
                  <Row k="Working dir">{info.workingDir}</Row>
                  <Row k="Entrypoint">
                    <span className="dkr-mono">{info.entrypoint.join(" ")}</span>
                  </Row>
                  <Row k="Command">
                    <span className="dkr-mono">{info.cmd.join(" ")}</span>
                  </Row>
                  <Row k="Privileged">{info.privileged ? "yes" : undefined}</Row>
                  <Row k="Memory limit">{info.memoryLimit ? fmtSize(info.memoryLimit) : undefined}</Row>
                  <Row k="CPU limit">{info.nanoCpus ? `${(info.nanoCpus / 1e9).toFixed(2)} cores` : undefined}</Row>
                </dl>
              </div>

              <div className="rt-box rt-c6">
                <h3>
                  Network
                  <span>{info.networkMode}</span>
                </h3>
                <div className="rt-scroll">
                  <table className="rt-mini">
                    <thead>
                      <tr>
                        <th>Network</th>
                        <th>IP address</th>
                        <th>Gateway</th>
                        <th>MAC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {info.networks.length === 0 && (
                        <tr>
                          <td colSpan={4} className="dkr-dim">
                            Not attached to a network.
                          </td>
                        </tr>
                      )}
                      {info.networks.map((n) => (
                        <tr key={n.network}>
                          <td>{n.network}</td>
                          <td className="mono">{n.ip || "—"}</td>
                          <td className="mono">{n.gateway || "—"}</td>
                          <td className="mono">{n.mac || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3>Ports</h3>
                {info.ports.length ? (
                  <div className="dkr-portlist">
                    <PortLinks ports={info.ports} address={address} />
                    <span className="dkr-dim">{info.ports.filter((p) => !p.public).map((p) => `${p.private}/${p.type}`).join(", ")}</span>
                  </div>
                ) : (
                  <span className="dkr-dim">No ports.</span>
                )}
              </div>

              <div className="rt-box rt-c12">
                <h3>
                  Volumes &amp; mounts
                  <span>{info.mounts.length}</span>
                </h3>
                {info.mounts.length === 0 ? (
                  <span className="dkr-dim">No mounts.</span>
                ) : (
                  <div className="rt-scroll">
                    <table className="rt-mini">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Source</th>
                          <th>Container path</th>
                          <th>Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {info.mounts.map((m) => (
                          <tr key={`${m.destination}-${m.source}`}>
                            <td>{m.type}</td>
                            <td className="mono">{m.name || m.source}</td>
                            <td className="mono">{m.destination}</td>
                            <td>{m.rw ? "read-write" : "read-only"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="rt-box rt-c6">
                <h3>
                  Environment
                  <button type="button" className="dkr-link" onClick={() => setReveal((v) => !v)}>
                    {reveal ? <EyeSlashIcon /> : <EyeIcon />}
                    {reveal ? "Hide values" : "Show values"}
                  </button>
                </h3>
                {info.env.length === 0 ? (
                  <span className="dkr-dim">No environment variables.</span>
                ) : (
                  <dl className="rt-kv dkr-env">
                    {info.env.map((e) => {
                      const i = e.indexOf("=");
                      const k = i < 0 ? e : e.slice(0, i);
                      const v = i < 0 ? "" : e.slice(i + 1);
                      return (
                        <React.Fragment key={e}>
                          <dt className="dkr-mono">{k}</dt>
                          <dd className="dkr-mono">{reveal ? v : v ? (SECRET_RE.test(k) || SECRET_RE.test(v) ? "••••••••" : "••••") : ""}</dd>
                        </React.Fragment>
                      );
                    })}
                  </dl>
                )}
              </div>

              <div className="rt-box rt-c6">
                <h3>
                  Labels
                  <span>{Object.keys(info.labels).length}</span>
                </h3>
                {Object.keys(info.labels).length === 0 ? (
                  <span className="dkr-dim">No labels.</span>
                ) : (
                  <dl className="rt-kv dkr-env">
                    {Object.entries(info.labels)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt className="dkr-mono">{k}</dt>
                          <dd className="dkr-mono">{v}</dd>
                        </React.Fragment>
                      ))}
                  </dl>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirm !== null}
        danger
        busy={run.isPending}
        title={confirm === "kill" ? "Kill container?" : "Remove container?"}
        message={
          <>
            {confirm === "kill" ? "SIGKILL will be sent immediately to " : "This container will be deleted (volumes are kept): "}
            <b>{name}</b>
          </>
        }
        confirmLabel={confirm === "kill" ? "Kill" : "Remove"}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && run.mutate(confirm)}
      />
    </div>
  );
};
